import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from '../../components/layout/AdminLayout';
import { Brain, Loader2, Save } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: API_URL, withCredentials: true });

const TIERS = ['starter', 'pro', 'premium', 'agency'];
const INT_FIELDS = [
  ['searches_per_day', 'Searches / day'],
  ['max_saved_items', 'Max saved items'],
  ['max_saved_searches', 'Max saved searches'],
  ['max_watchlists', 'Max watchlists'],
  ['max_plan_days', 'Max content-plan days'],
  ['briefing_retention_days', 'Briefing history retention (days)'],
];
const BOOL_FIELDS = [
  ['multi_source', 'Multi-Source Analysis'],
  ['build_campaign', 'Build Campaign'],
  ['historical_search', 'Historical Search'],
  ['flyer_export', 'Flyer Studio export'],
  ['social_export', 'Social Studio export'],
  ['competitive_intel', 'Competitive Intelligence'],
];

export default function AdminIntelligenceSettings() {
  const [limits, setLimits] = useState(null);
  const [cfg, setCfg] = useState({ enabled: true, free_source_only: false, provider: 'google_news' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/api/admin/intelligence/limits');
      setLimits(r.data.limits);
      setCfg(r.data.config);
    } catch (e) { toast.error('Could not load Intelligence settings (admin only)'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setVal = (tier, field, value) => setLimits((s) => ({ ...s, [tier]: { ...s[tier], [field]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { enabled: cfg.enabled, free_source_only: cfg.free_source_only, provider: cfg.provider };
      TIERS.forEach((t) => {
        const l = limits[t];
        payload[t] = {
          searches_per_day: l.searches_per_day === '' ? null : l.searches_per_day,
          max_saved_items: l.max_saved_items === '' ? null : l.max_saved_items,
          max_saved_searches: l.max_saved_searches === '' ? null : l.max_saved_searches,
          max_watchlists: l.max_watchlists === '' ? null : l.max_watchlists,
          max_plan_days: l.max_plan_days || 90,
          briefing_retention_days: (l.briefing_retention_days === '' || l.briefing_retention_days == null) ? null : l.briefing_retention_days,
          multi_source: !!l.multi_source, build_campaign: !!l.build_campaign,
          historical_search: !!l.historical_search, flyer_export: !!l.flyer_export,
          social_export: !!l.social_export, competitive_intel: !!l.competitive_intel,
        };
      });
      const r = await api.post('/api/admin/intelligence/limits', payload);
      setLimits(r.data.limits); setCfg(r.data.config);
      toast.success('Intelligence settings saved');
    } catch (e) { toast.error('Could not save'); } finally { setSaving(false); }
  };

  if (!limits) return <AdminLayout><div className="py-20 text-center"><Loader2 className="animate-spin mx-auto text-[#94A3B8]" /></div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto px-4 py-6" data-testid="intel-admin">
        <h1 className="text-2xl font-bold text-[#0A0F1C] flex items-center gap-2 mb-1"><Brain className="text-[#1E5BA8]" /> Content Intelligence — Settings</h1>
        <p className="text-sm text-[#64748B] mb-5">Per-plan limits &amp; feature access. Leave a number blank for unlimited. Admin always bypasses every limit.</p>

        <div className="bg-white rounded-2xl border border-[#E2E8F0] p-5 mb-5 flex flex-wrap items-center gap-5">
          <label className="flex items-center gap-2 text-sm font-bold text-[#0A0F1C]"><input type="checkbox" checked={cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} data-testid="intel-cfg-enabled" /> Engine enabled</label>
          <label className="flex items-center gap-2 text-sm font-bold text-[#0A0F1C]"><input type="checkbox" checked={cfg.free_source_only} onChange={(e) => setCfg({ ...cfg, free_source_only: e.target.checked })} data-testid="intel-cfg-freeonly" /> Free-source-only mode</label>
          <label className="flex items-center gap-2 text-sm font-bold text-[#0A0F1C]">Provider
            <select value={cfg.provider} onChange={(e) => setCfg({ ...cfg, provider: e.target.value })} data-testid="intel-cfg-provider" className="px-3 py-1.5 rounded-lg border border-[#E2E8F0] text-sm">
              <option value="google_news">Google News (free, live)</option>
              <option value="gdelt">GDELT (free, rate-limited)</option>
            </select>
          </label>
        </div>

        <div className="overflow-x-auto bg-white rounded-2xl border border-[#E2E8F0] p-5">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wide text-[#64748B]">
              <th className="py-2 pr-3">Setting</th>{TIERS.map((t) => <th key={t} className="py-2 px-3 capitalize">{t}</th>)}
            </tr></thead>
            <tbody>
              {INT_FIELDS.map(([f, label]) => (
                <tr key={f} className="border-t border-[#F1F5F9]">
                  <td className="py-2 pr-3 font-semibold text-[#475569]">{label}</td>
                  {TIERS.map((t) => (
                    <td key={t} className="py-2 px-3">
                      <input type="number" value={limits[t][f] ?? ''} placeholder="∞" data-testid={`intel-${t}-${f}`}
                        onChange={(e) => setVal(t, f, e.target.value === '' ? '' : Number(e.target.value))}
                        className="w-24 px-2 py-1.5 rounded-lg border border-[#E2E8F0] text-sm" />
                    </td>
                  ))}
                </tr>
              ))}
              {BOOL_FIELDS.map(([f, label]) => (
                <tr key={f} className="border-t border-[#F1F5F9]">
                  <td className="py-2 pr-3 font-semibold text-[#475569]">{label}</td>
                  {TIERS.map((t) => (
                    <td key={t} className="py-2 px-3"><input type="checkbox" checked={!!limits[t][f]} data-testid={`intel-${t}-${f}`} onChange={(e) => setVal(t, f, e.target.checked)} /></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <button onClick={save} disabled={saving} data-testid="intel-admin-save" className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#1E5BA8] text-white text-sm font-bold hover:bg-[#1A4F94] disabled:opacity-60">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save settings
        </button>
      </div>
    </AdminLayout>
  );
}
